import { Button, Card, CardContent, Container, Grid, Paper, Typography } from "@material-ui/core";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { Cancel, KeyboardCapslock as Export } from "@material-ui/icons";
import { App } from "app-interfaces";
import { addDays, format } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";

import { useStateValue } from "../../config/AppContext";
import { Action } from "../../types/StaticTypes";
import { DatePickerModal } from "../Modals";

type StatsProps = {
  range: { from: string; to: string };
  total: number;
  completed: number;
  upcoming: number;
  cancelled: number;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    paper: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(2),
      textAlign: "left",
      color: theme.palette.text.secondary,
      "& h5": {
        color: "#9B9B9B",
        fontSize: 16,
        padding: "5px 10px",
      },
      "& h6": {
        fontSize: 14,
        marginTop: theme.spacing(3),
      },
      "& p": {
        color: theme.palette.text.primary,
        fontSize: 15,
      },
      "& span": {
        display: "block",
        color: theme.palette.text.secondary,
        fontSize: 17,
        [theme.breakpoints.down("md")]: {
          fontSize: 16,
        },
      },
      "& svg": {
        fill: "#D0021B",
        marginLeft: "calc(100% - 150px)",
        [theme.breakpoints.down("md")]: {
          marginTop: -3,
          marginLeft: "calc(100% - 140px)",
        },
      },
    },
    actions: {
      marginTop: theme.spacing(3),
      [theme.breakpoints.down("md")]: {
        marginBottom: theme.spacing(2),
      },
      "& button": {
        backgroundColor: "var(--text-primary)",
        fontSize: 17,
        height: 56,
        textTransform: "none",
        padding: 30,
        whiteSpace: "nowrap",
        "& svg": {
          marginLeft: "calc(100% - 165px)",
        },
        "&:hover": {
          backgroundColor: "var(--text-primary)",
        },
      },
    },
  })
);

const StatisticsComponent: React.FC = () => {
  const classes = useStyles();
  const { Methods } = useStateValue() as { Methods: App.Methods };
  const initialStats: StatsProps = {
    range: { from: "", to: "" },
    total: 0,
    completed: 0,
    upcoming: 0,
    cancelled: 0,
  };
  const StatsReducer: React.Reducer<StatsProps, Action> = (stats, action) => {
    switch (action.type) {
      case "init":
        stats = action.payload;
        break;
      case "reset":
        stats = initialStats;
        break;
      case "update":
        stats = action.payload;
        break;
      default:
        break;
    }
    return stats;
  };
  const [stats, dispatch] = React.useReducer(StatsReducer, initialStats);

  React.useEffect(() => {
    const from = format(addDays(new Date(), -31), "yyyy-MM-dd");
    const to = format(addDays(new Date(), -1), "yyyy-MM-dd");
    Methods.getStats({ from, to }).then((data) => {
      let total = 0;
      let completed = 0;
      let upcoming = 0;
      let cancelled = 0;
      if (!data) return dispatch({ type: "reset" });
      data[0].stats.forEach((stat: { state: string; count: number }) => {
        if (stat.state === "COMPLETED") completed += stat.count;
        if (stat.state === "CANCELLED") cancelled += stat.count;
        if (stat.state === "NONE") upcoming += stat.count;
        total += stat.count;
      });
      return dispatch({
        type: "init",
        payload: {
          total,
          completed,
          upcoming,
          cancelled,
          range: {
            from,
            to,
          },
        },
      });
    });
  }, [Methods]);

  return (
    <div id="Statistics">
      <Grid container>
        <Grid item xs={12} md={8}>
          <Paper className={classes.paper}>
            <DateRageSection stats={stats} dispatch={dispatch} />
            <StatsSection stats={stats} dispatch={dispatch} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Container className={classes.actions}>
            <ActionSection stats={stats} dispatch={dispatch} />
          </Container>
        </Grid>
      </Grid>
    </div>
  );
};

const DateRageSection: React.FC<{
  stats: StatsProps;
  dispatch: React.Dispatch<Action>;
}> = ({ stats, dispatch }) => {
  const { t } = useTranslation();
  const { Methods } = useStateValue() as { Methods: App.Methods };
  const [dateModal, showDateModal] = React.useState(false);

  // Submit Event handler
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Close Modal
    showDateModal(false);
    const formData = new FormData(e.currentTarget);

    const from = formData.get("from");
    const to = formData.get("to");
    console.log("formdata", formData, from, to);
    if (!from || typeof from !== "string" || !to || typeof to !== "string") return;

    const data = await Methods.getStats({ from, to });
    if (!data) return dispatch({ type: "reset" });

    const payload = {
      total: 0,
      completed: 0,
      upcoming: 0,
      cancelled: 0,
      range: {
        from: format(new Date(from), "yyyy-MM-dd"),
        to: format(new Date(to), "yyyy-MM-dd"),
      },
    };
    data[0].stats.forEach((stat: { state: string; count: number }) => {
      if (stat.state === "COMPLETED") payload.completed += stat.count;
      if (stat.state === "CANCELLED") payload.cancelled += stat.count;
      if (stat.state === "NONE") payload.upcoming += stat.count;
      payload.total += stat.count;
    });
    return dispatch({
      type: "update",
      payload,
    });
  };

  // Clear Event handler
  const handleClear = () => {
    if (!stats) return showDateModal(false);
    return dispatch({ type: "reset" });
  };

  return (
    <>
      <Typography variant="subtitle1" gutterBottom style={{ marginTop: 0 }}>
        {t("show sessions for")}
      </Typography>
      <hr />
      <Grid container style={{ cursor: "pointer" }}>
        <Grid item xs={6}>
          <Typography variant="body1" onClick={() => showDateModal(true)}>
            {t("Choose period")}
          </Typography>
        </Grid>
        <Grid container item xs={6} direction="row">
          <Typography variant="body2" component="span" onClick={() => showDateModal(true)}>
            {stats.range.from ? `${format(new Date(stats.range.from), "d MMM")}` : t("Choose")} -{" "}
            {stats.range.to ? `${format(new Date(stats.range.to), "d MMM")}` : t("period")}
          </Typography>
          <Cancel onClick={handleClear} />
        </Grid>
      </Grid>
      <DatePickerModal
        show={dateModal}
        onHide={() => showDateModal(false)}
        onSubmit={handleSubmit}
        startDate={stats.range.from}
        endDate={stats.range.to}
      />
    </>
  );
};

const StatsSection: React.FC<{
  stats: StatsProps;
  dispatch: React.Dispatch<Action>;
}> = ({ stats, dispatch }) => {
  const { t } = useTranslation();
  if (!stats.range.from || !stats.range.to) return <></>;
  return (
    <>
      <hr />
      <Typography variant="subtitle1" gutterBottom style={{ marginTop: 0, marginBottom: 15 }}>
        {t("Stats for")} {format(new Date(stats.range.from), "MMM dd, yyyy")} -{" "}
        {format(new Date(stats.range.to), "MMM dd, yyyy")}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={6} md={3}>
          <Card elevation={0} variant="outlined" style={{ height: 75 }}>
            <Typography variant="body1" component="h5">
              {t("Total sessions")}
            </Typography>
            <CardContent style={{ padding: 14, marginTop: -15 }}>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card elevation={0} variant="outlined" style={{ height: 75 }}>
            <Typography variant="body1" component="h5">
              {t("Upcoming")}
            </Typography>
            <CardContent style={{ padding: 14, marginTop: -15 }}>
              <Typography variant="h4">{stats.upcoming}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card elevation={0} variant="outlined" style={{ height: 75 }}>
            <Typography variant="body1" component="h5">
              {t("Completed")}
            </Typography>
            <CardContent style={{ padding: 14, marginTop: -15 }}>
              <Typography variant="h4">{stats.completed}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card elevation={0} variant="outlined" style={{ height: 75 }}>
            <Typography variant="body1" component="h5">
              {t("Cancelled")}
            </Typography>
            <CardContent style={{ padding: 14, marginTop: -15 }}>
              <Typography variant="h4">{stats.cancelled}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

const ActionSection: React.FC<{
  stats: StatsProps;
  dispatch: React.Dispatch<Action>;
}> = ({ stats, dispatch }) => {
  const { t } = useTranslation();
  const { Methods } = useStateValue() as { Methods: App.Methods };
  // Export statistics
  const handleExportStats = (e: any) => {
    if (!stats || !stats.range) return;
    const params = {
      from: stats.range.from,
      to: stats.range.to,
    };
    Methods.exportStats(params).then(() => {
      return Methods.setAppState({
        modalTitle: t("Statistics exported"),
        modalMessage: t("Data exported!"),
        showModal: true,
      });
    });
  };
  if (!stats.range.from || !stats.range.to) return <></>;
  return (
    <Card elevation={2}>
      <Button fullWidth variant="contained" color="primary" onClick={handleExportStats}>
        {t("Export statistics")} <Export />
      </Button>
    </Card>
  );
};

export default StatisticsComponent;
